import {
  cloneElement,
  type FocusEventHandler,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useState,
} from "react"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

type OverflowTriggerProps = {
  onFocus?: FocusEventHandler<HTMLElement>
  onMouseEnter?: MouseEventHandler<HTMLElement>
}

// The one tooltip surface: the float skin, hugging its content (never wider
// than it needs, up to the caller's cap). Text by `label`, or any `content`
// (a list's pills), which always opens.
export const TerminalOverflowTooltip = ({
  label,
  content,
  children,
  shouldForce = false,
  side = "top",
  align = "start",
  sideOffset = 6,
  collisionPadding = 12,
  contentClassName,
}: {
  label: string | undefined
  content?: ReactNode
  children: ReactElement<OverflowTriggerProps>
  shouldForce?: boolean
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  collisionPadding?: number
  contentClassName?: string
}) => {
  const { theme } = useTerminalTheme()
  const [isOpen, setIsOpen] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)

  if (!label && !content) return children

  const updateEnabled = (element: HTMLElement) => {
    const isNextEnabled =
      shouldForce ||
      Boolean(content) ||
      element.scrollWidth > element.clientWidth + 1 ||
      element.scrollHeight > element.clientHeight + 1
    setIsEnabled(isNextEnabled)
    if (!isNextEnabled) setIsOpen(false)
  }

  const trigger = cloneElement(children, {
    onFocus: (event) => {
      children.props.onFocus?.(event)
      updateEnabled(event.currentTarget)
    },
    onMouseEnter: (event) => {
      children.props.onMouseEnter?.(event)
      updateEnabled(event.currentTarget)
    },
  })

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip
        open={isEnabled && isOpen}
        onOpenChange={(isNextEnabled) =>
          setIsOpen(isEnabled ? isNextEnabled : false)
        }
      >
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          data-terminal-theme={theme}
          className={cn(
            "bg-popover border-border text-foreground shadow-tooltip w-max rounded-sm border px-2.5 py-1.5",
            contentClassName,
            theme === "dark" && "dark"
          )}
        >
          {content ?? label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
